import { Box, Button } from '@dixa/lib-shared-habla';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { Children, ReactNode, useMemo } from 'react';
import { useToggle } from 'react-use';

type PartialChildrenRenderTogglerProps = {
  /** Whether all children are currently shown. */
  showAll: boolean;
  /** Function called to toggle showing all children. */
  toggleShowAll: () => void;
};

type PartialChildrenProps = {
  /** The maximum number of children to display initially. */
  limit: number;
  /** Function called to render the toggler. */
  renderToggler?: (props: PartialChildrenRenderTogglerProps) => ReactNode;
} & PropsWithChildren;

/**
 * A component that given a list of children and a `limit` integer, will render
 * only the first `limit` children and render a toggle button if necessary to
 * show the rest of the children.
 *
 * By default the component renders a default toggle button, but you can pass a
 * `renderToggler` function prop to render your own custom toggler based on the
 * `showAll` state and the `toggleShowAll` function.
 */
export const PartialChildren = ({
  children,
  limit,
  renderToggler,
}: PartialChildrenProps): JSX.Element => {
  const total = Children.count(children);
  const showToggler = limit < total;
  const [showAll, toggleShowAll] = useToggle(false);

  const shownChildren = useMemo(
    () => Children.toArray(children).slice(0, showAll ? total : limit),
    [children, limit, showAll, total],
  );

  const hiddenChildrenCount = useMemo(
    () => total - shownChildren.length,
    [shownChildren.length, total],
  );

  if (!showToggler) return <>{shownChildren}</>;

  return (
    <>
      {shownChildren}

      {typeof renderToggler === 'function' ? (
        renderToggler({
          showAll,
          toggleShowAll,
        })
      ) : (
        <Box flex="0 0 100%">
          <Button variant="tertiary" onClick={toggleShowAll}>
            {showAll ? `Show less` : `+ ${hiddenChildrenCount} more`}
          </Button>
        </Box>
      )}
    </>
  );
};
